import React from 'react';
import { motion, Variants } from 'framer-motion';
import objectiveImage from '../../../assets/images/arbventure/objective_image.webp';
import Constants from '../../../app/utils/constants';
import colors from '../../theme/color';

type ObjectiveCardProps = {
  children: React.ReactNode;
  className?: string;
  index?: number;
};

const ObjectiveCard = ({ children, className = '', index = 0 }: ObjectiveCardProps) => {
  const cardVariant: Variants = {
    hidden: { opacity: 0, x: 150 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.7, ease: 'easeOut', delay: index * 0.15 },
    },
  };

  return (
    <motion.div
      variants={cardVariant}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: false, amount: 0.3 }}
      whileHover={{ scale: 1.03 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      className={`rounded-[18px] p-[1px] shadow-[0_18px_34px_rgba(0,0,0,0.22)] min-[2560px]:p-[2px] ${className}`}
      style={{
        background:
          'linear-gradient(174deg, rgba(111,175,56,0.78) 0%,  rgba(111,175,56,0) 40%, rgba(111,175,56,0) 100%)',
      }}
    >
      <div className="flex min-h-[50px] items-center rounded-[17px] bg-[#050705] px-6 py-5 font-gilroy-medium text-[1.05rem] leading-[1.35] text-white md:min-h-[84px] md:px-7 md:py-3 md:text-[1.18rem] lg:min-h-[55px] lg:text-[1rem] xl:min-h-[70px] xl:text-[1.36rem] 2xl:min-h-[100px] 2xl:text-[1.85rem] min-[2560px]:px-12 min-[2560px]:py-10 min-[2560px]:text-[2.6rem]">
        <span className="mr-3" aria-hidden="true">
          &bull;
        </span>
        {children}
      </div>
    </motion.div>
  );
};

const ObjectiveSection = () => {
  const { arbventureObjective } = Constants;
  const [firstItem, secondItem, thirdItem, fourthItem] = arbventureObjective.items;

  const imageVariant: Variants = {
    hidden: { opacity: 0, y: -150 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: 'easeIn' },
    },
  };

  const textBlockVariant: Variants = {
    hidden: { opacity: 0, x: 150 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: 'easeIn' },
    },
  };

  return (
    <section className="relative overflow-hidden pb-18 pt-4 md:pb-15 md:pt-8 lg:pb-30 lg:pt-10 2xl:pb-38">
      <div
        className="pointer-events-none absolute left-[48%] top-[48%] h-[10rem] w-[10rem] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[54px] md:h-[14rem] md:w-[14rem] md:blur-[64px] lg:h-[18rem] lg:w-[18rem] xl:h-[22rem] xl:w-[22rem] min-[2560px]:h-[35rem] min-[2560px]:w-[35rem] min-[2560px]:blur-[100px]"
        style={{ backgroundColor: 'rgba(111,175,56,0.16)' }}
      />

      <div className="mx-auto w-full max-w-[1380px] px-5 md:px-8 lg:px-12 xl:max-w-[1520px] 2xl:max-w-[1820px] 2xl:px-16 min-[2056px]:max-w-[2100px] min-[2056px]:px-20 min-[2560px]:max-w-[2400px] min-[2560px]:px-24">
        <div className="relative z-10 grid mt-15 md:mt-15 lg:mt-0 items-start gap-10 md:grid-cols-[minmax(320px,560px)_minmax(460px,1fr)] md:gap-0 lg:grid-cols-[minmax(320px,560px)_minmax(460px,1fr)] lg:gap-12 xl:grid-cols-[minmax(420px,650px)_minmax(560px,1fr)] xl:gap-16 2xl:grid-cols-[minmax(560px,780px)_minmax(720px,1fr)] min-[2560px]:grid-cols-[minmax(750px,1050px)_minmax(950px,1fr)] min-[2560px]:gap-24">

          {/* LEFT — image top-down, then third card from right */}
          <div className="order-2 flex flex-col items-center lg:order-1 lg:items-start lg:pl-10 xl:pl-14 2xl:pl-[4.5rem] min-[2560px]:pl-20">

            {/* IMAGE — top to bottom */}
            <motion.img
              src={objectiveImage}
              alt="ARB Venture real estate management objective illustration"
              variants={imageVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.3 }}
              className="h-auto w-full max-w-[260px] object-contain md:max-w-[310px] lg:max-w-[370px] xl:max-w-[430px] 2xl:max-w-[650px] min-[2560px]:max-w-[780px]"
            />

            <ObjectiveCard
              className="mt-8 w-full max-w-[360px] lg:max-w-[440px] xl:max-w-[520px] 2xl:max-w-[640px] min-[2560px]:mt-16 min-[2560px]:max-w-[900px]"
              index={0}
            >
              {thirdItem}
            </ObjectiveCard>
          </div>

          {/* RIGHT — badge pulses, intro text + cards from right one by one */}
          <motion.div
            variants={textBlockVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.2 }}
            className="order-1 mx-auto w-full max-w-[640px] text-center lg:order-2 lg:mx-0 lg:text-left xl:max-w-[700px] 2xl:max-w-[900px] min-[2560px]:max-w-[1200px]"
          >
            {/* BADGE — continuously pulses */}
            <motion.div
              className="inline-flex items-center justify-center px-6 py-3 lg:px-7 lg:py-3.5 2xl:px-9 2xl:py-4 min-[2560px]:px-12 min-[2560px]:py-6"
              style={{ backgroundColor: colors.arbVentureAccent }}
            >
              <span className="font-gilroy-bold text-[2rem] font-semibold leading-none text-white lg:text-[2.35rem] xl:text-[2.7rem] 2xl:text-[3.25rem] min-[2056px]:text-[3.8rem] min-[2560px]:text-[4.8rem]">
                {arbventureObjective.badgeText}
              </span>
            </motion.div>

            <p className="mt-4 font-gilroy-medium text-[0.95rem] leading-[1.5] md:text-[1rem] lg:text-[1.05rem] xl:text-[1.12rem] 2xl:mt-5 2xl:text-[1.48rem] min-[2560px]:mt-10 min-[2560px]:text-[2.2rem]">
              {arbventureObjective.introText}
            </p>

            {/* CARDS — staggered one by one from right */}
            <div className="mt-6 grid gap-5 lg:mt-8 lg:gap-6 2xl:gap-8 min-[2560px]:mt-12 min-[2560px]:gap-12">
              {[firstItem, secondItem, fourthItem].map((item, i) => (
                <ObjectiveCard key={item} index={i}>
                  {item}
                </ObjectiveCard>
              ))}
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default ObjectiveSection;